import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { isGroup, navSectionsForRole, type NavEntry, type NavGroup } from '@/config/nav';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { sectionDefaultOpen, useNavCollapse } from './navCollapse';

interface SidebarNavProps {
  /** Called after a leaf link is clicked — used to close the mobile drawer. */
  onNavigate?: () => void;
}

const leafClass = (isActive: boolean) =>
  cn(
    'block rounded-md px-3 py-1.5 text-sm transition-colors',
    'hover:bg-accent hover:text-accent-foreground',
    isActive ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground',
  );

/** The active leaf is the longest `to` that prefixes the current path. So a
 *  detail route (/crm/leads/123) keeps its list item (/crm/leads) highlighted,
 *  and nested siblings like /crm/deals/board win over /crm/deals. `/` matches
 *  only the exact path so Dashboard isn't perpetually active. */
function pickActivePath(pathname: string, paths: string[]): string | null {
  let best: string | null = null;
  for (const to of paths) {
    const matches =
      to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(to + '/');
    if (matches && (best === null || to.length > best.length)) best = to;
  }
  return best;
}

function collectLeafPaths(sections: ReturnType<typeof navSectionsForRole>): string[] {
  const out: string[] = [];
  for (const section of sections) {
    for (const entry of section.entries) {
      if (isGroup(entry)) out.push(...entry.children.map((c) => c.to));
      else out.push(entry.to);
    }
  }
  return out;
}

function NavModule({
  group,
  activeTo,
  onNavigate,
}: {
  group: NavGroup;
  activeTo: string | null;
  onNavigate?: () => void;
}) {
  const { pathname } = useLocation();
  const active = pathname === group.basePath || pathname.startsWith(group.basePath + '/');

  // Derive open state during render (no setState-in-effect): follow the active
  // route by default; once the user toggles, their choice takes over.
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? active;

  const Icon = group.icon;

  return (
    <Collapsible open={open} onOpenChange={setUserOpen}>
      <CollapsibleTrigger
        className={cn(
          'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <Icon className="size-4 shrink-0" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronRight className={cn('size-4 shrink-0 transition-transform', open && 'rotate-90')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-0.5 ml-4 flex flex-col gap-0.5 border-l pl-3">
        {group.children.map((child) => (
          <NavLink
            key={child.to}
            to={child.to}
            end
            onClick={onNavigate}
            className={leafClass(child.to === activeTo)}
          >
            {child.label}
          </NavLink>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function renderEntry(entry: NavEntry, activeTo: string | null, onNavigate?: () => void) {
  if (isGroup(entry)) {
    return (
      <NavModule key={entry.label} group={entry} activeTo={activeTo} onNavigate={onNavigate} />
    );
  }
  return (
    <NavLink
      key={entry.to}
      to={entry.to}
      end
      onClick={onNavigate}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        entry.to === activeTo ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
      )}
    >
      <entry.icon className="size-4 shrink-0" />
      {entry.label}
    </NavLink>
  );
}

/** A sidebar section. Sections with a heading are collapsible and remember their
 * open/closed state across reloads (see navCollapse). */
function NavSection({
  heading,
  entries,
  activeTo,
  onNavigate,
}: {
  heading?: string;
  entries: NavEntry[];
  activeTo: string | null;
  onNavigate?: () => void;
}) {
  const openByHeading = useNavCollapse((s) => s.openByHeading);
  const setOpen = useNavCollapse((s) => s.setOpen);

  if (!heading) {
    return (
      <div className="flex flex-col gap-1">
        {entries.map((e) => renderEntry(e, activeTo, onNavigate))}
      </div>
    );
  }

  const open = openByHeading[heading] ?? sectionDefaultOpen(heading);

  return (
    <Collapsible open={open} onOpenChange={(o) => setOpen(heading, o)} className="flex flex-col gap-1">
      <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex w-full items-center gap-2 px-3 pt-1 text-xs font-semibold tracking-wide uppercase transition-colors">
        <span className="flex-1 text-left">{heading}</span>
        <ChevronRight className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-90')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-1">
        {entries.map((e) => renderEntry(e, activeTo, onNavigate))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function SidebarNav({ onNavigate }: SidebarNavProps) {
  const role = useSession((s) => s.role);
  const sections = navSectionsForRole(role);
  const { pathname } = useLocation();
  const activeTo = pickActivePath(pathname, collectLeafPaths(sections));

  return (
    <ScrollArea className="h-full">
      <nav className="flex flex-col gap-4 p-3" aria-label="Primary">
        {sections.map((section, i) => (
          <NavSection
            key={section.heading ?? `section-${i}`}
            heading={section.heading}
            entries={section.entries}
            activeTo={activeTo}
            onNavigate={onNavigate}
          />
        ))}
      </nav>
    </ScrollArea>
  );
}
